import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { MaskedLine, FadeIn, Reveal } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import TerminalCard from "@/components/TerminalCard";
import BentoCard from "@/components/BentoCard";
import CTABand from "@/components/CTABand";
import Seo from "@/components/Seo";
import { SERVICES, HERO_IMAGE, ACADEMY_IMAGE, ABOUT_IMAGE } from "@/data/site";

const Home = () => {  const heroRef = useRef(null);
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 700], [0, 130]);

  return (
    <div data-testid="home-page">
      <Seo
        title="TecKube | Modern Challenges, Innovative Solutions"
        description="TecKube helps businesses develop applications, manage cloud infrastructure and improve software delivery — from cloud migration and Kubernetes to cybersecurity."
      />
      {/* HERO */}
      <section
        ref={heroRef}
        className="relative flex min-h-[94vh] items-center overflow-hidden pb-20 pt-32"
        data-testid="home-hero"
      >
        <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10" aria-hidden="true">
          <img
            src={HERO_IMAGE}
            alt=""
            className="h-[115%] w-full object-cover"
            fetchpriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white/75 via-white/85 to-paper" />
        </motion.div>

        <div
          aria-hidden="true"
          className="pointer-events-none absolute -left-40 top-1/3 -z-10 h-[500px] w-[500px] rounded-full bg-primary/[0.08] blur-3xl"
        />

        <div className="mx-auto grid w-full max-w-7xl items-center gap-16 px-6 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <FadeIn delay={0.1}>
              <span className="eyebrow" data-testid="hero-eyebrow">
                Cloud · DevOps · Applications · Security
              </span>
            </FadeIn>
            <h1 className="mt-8 font-display text-[clamp(2.6rem,6.2vw,5.4rem)] font-semibold leading-[0.98] tracking-tightest text-ink">
              <MaskedLine delay={0.15}>Build, deploy</MaskedLine>
              <MaskedLine delay={0.27}>and secure your</MaskedLine>
              <MaskedLine delay={0.39}>
                <span className="text-primary">digital infrastructure.</span>
              </MaskedLine>
            </h1>
            <FadeIn delay={0.6}>
              <p className="mt-8 max-w-xl text-lg leading-relaxed text-soot md:text-xl">
                TecKube helps businesses develop applications, manage cloud infrastructure and
                improve software delivery.
              </p>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Link
                  to="/services"
                  data-testid="hero-explore-services-button"
                  className="btn btn-primary"
                >
                  Explore Our Services
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/contact"
                  data-testid="hero-discuss-project-button"
                  className="btn btn-ghost"
                >
                  Discuss Your Project
                  <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            </FadeIn>
            <FadeIn delay={0.8}>
              <p
                className="mt-12 flex items-center gap-4 font-display text-[13px] font-semibold uppercase tracking-[0.28em] text-soot"
                data-testid="hero-tagline"
              >
                <span className="h-px w-10 bg-primary" />
                Modern Challenges, Innovative Solutions
              </p>
            </FadeIn>
          </div>

          <div className="lg:col-span-5">
            <TerminalCard />
          </div>
        </div>

        <div
          aria-hidden="true"
          className="absolute bottom-8 left-6 hidden items-center gap-3 md:left-12 lg:flex"
        >
          <span className="relative h-10 w-[2px] overflow-hidden bg-slate-200">
            <span className="scroll-cue absolute inset-0 bg-primary" />
          </span>
          <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-soot">
            Scroll
          </span>
        </div>
      </section>

      <Marquee />

      {/* APPROACH */}
      <section className="py-24 md:py-36" data-testid="approach-section">
        <div className="mx-auto grid max-w-7xl gap-14 px-6 md:grid-cols-12">
          <div className="md:col-span-5">
            <Reveal>
              <span className="eyebrow">Technology that supports your business</span>
              <h2 className="mt-7 font-display text-[clamp(1.9rem,3.4vw,3rem)] font-semibold leading-[1.06] tracking-tight text-ink">
                Your applications, infrastructure and security need to work together.
              </h2>
            </Reveal>
          </div>
          <div className="space-y-0 md:col-span-6 md:col-start-7">
            {[
              "TecKube brings these disciplines into one service offering, helping you move from an initial requirement to a working solution that your team can operate and maintain.",
              "Whether you are moving to the cloud, improving deployment processes or developing an application, we start with your business needs and technical environment.",
              "One team across cloud, containers, applications and cybersecurity — from first conversation to a working, maintainable solution.",
            ].map((text, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="flex gap-6 border-t border-slate-200 py-8 first:border-t-0 first:pt-0 md:first:pt-8">
                  <span className="font-mono text-xs text-primary">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <p className="text-[17px] leading-relaxed text-soot md:text-lg">{text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section className="pb-24 md:pb-36" data-testid="services-bento-section">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-12 flex flex-wrap items-end justify-between gap-6 md:mb-16">
            <Reveal>
              <span className="eyebrow">What we do</span>
              <h2 className="mt-7 max-w-xl font-display text-[clamp(1.9rem,3.4vw,3rem)] font-semibold leading-[1.06] tracking-tight text-ink">
                Four disciplines, one service offering.
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <Link
                to="/services"
                data-testid="bento-view-all-link"
                className="group inline-flex items-center gap-2 font-display text-[15px] font-semibold text-primary"
              >
                View all services
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
          <div className="grid gap-5 md:grid-cols-12">
            {SERVICES.map((service, i) => (
              <BentoCard key={service.slug} service={service} index={i} wide={i === 0 || i === 3} />
            ))}
          </div>
        </div>
      </section>

      {/* ACADEMY */}
      <section className="noise relative overflow-hidden bg-night py-24 md:py-36" data-testid="academy-section">
        <div
          aria-hidden="true"
          className="absolute -left-40 top-0 h-[420px] w-[420px] rounded-full bg-primary/20 blur-3xl"
        />
        <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-6 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal>
              <span className="eyebrow-dark">TecKube Academy</span>
              <h2 className="mt-7 max-w-2xl font-display text-[clamp(1.9rem,3.6vw,3.2rem)] font-semibold leading-[1.05] tracking-tight text-white">
                Sharing knowledge in Cloud Computing, DevOps and Cybersecurity.
              </h2>
              <p className="mt-7 max-w-xl text-lg leading-relaxed text-white/65">
                TecKube Academy delivered training across these three technical disciplines. The
                Academy forms part of TecKube&rsquo;s history of combining technology services
                with technical education.
              </p>
              <Link
                to="/academy"
                data-testid="academy-discover-button"
                className="btn btn-light mt-10"
              >
                Discover TecKube Academy
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.15}>
              <div className="group overflow-hidden rounded-3xl border border-white/10">
                <img
                  src={ACADEMY_IMAGE}
                  alt="TecKube Academy training session"
                  loading="lazy"
                  className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.05]"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ABOUT TEASER */}
      <section className="py-24 md:py-36" data-testid="about-teaser-section">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="group relative overflow-hidden rounded-3xl">
                <img
                  src={ABOUT_IMAGE}
                  alt="The TecKube team at work"
                  loading="lazy"
                  className="aspect-[4/5] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/25 to-transparent" />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal>
              <span className="eyebrow">About TecKube</span>
              <h2 className="mt-7 font-display text-[clamp(1.8rem,3.2vw,2.8rem)] font-semibold leading-[1.1] tracking-tight text-ink">
                We connect the work of building applications with the infrastructure and
                processes needed to run them.
              </h2>
              <p className="mt-7 text-lg leading-relaxed text-soot">
                TecKube is a technology services business working across cloud infrastructure,
                software delivery, application development and cybersecurity.
              </p>
              <Link
                to="/about"
                data-testid="about-more-link"
                className="group mt-9 inline-flex items-center gap-2 font-display text-[15px] font-semibold text-primary"
              >
                More about TecKube
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      <CTABand />
    </div>
  );
};

export default Home;
